/**
 * ThemeContext — 3-way color-mode switcher for the admin app.
 *
 * Modes:
 *   'auto'   → follow the OS `prefers-color-scheme` (default)
 *   'light'  → force light
 *   'dark'   → force dark
 *
 * Applies/removes a `.dark` class on `<html>`. Pairs with
 * `darkMode: 'class'` in tailwind.config.js so existing `dark:` variants
 * light up everywhere without any other wiring. The Auto mode keeps a
 * matchMedia listener so a system-level toggle is picked up live.
 *
 * A parallel pre-hydration script in `public/index.html` sets the class
 * before React mounts to avoid a flash-of-wrong-theme (FOWT).
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'cp-color-mode';
const MODES = ['auto', 'light', 'dark'];

const readStored = () => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    return MODES.includes(raw) ? raw : 'auto';
  } catch {
    return 'auto';
  }
};

const writeStored = mode => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // iOS Safari private mode / storage disabled — swallow
  }
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveEffective = mode => {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return systemPrefersDark() ? 'dark' : 'light';
};

const applyClass = effective => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (effective === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Let native form controls / scrollbars use the right palette
  root.style.colorScheme = effective;
};

const ThemeContext = createContext({
  mode: 'auto',
  effective: 'light',
  setMode: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => readStored());
  const [effective, setEffective] = useState(() => resolveEffective(readStored()));

  // Apply on mode change + persist
  useEffect(() => {
    const next = resolveEffective(mode);
    setEffective(next);
    applyClass(next);
    writeStored(mode);
  }, [mode]);

  // In auto mode, react to OS-level changes
  useEffect(() => {
    if (mode !== 'auto' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = mql.matches ? 'dark' : 'light';
      setEffective(next);
      applyClass(next);
    };
    // Older Safari uses addListener
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [mode]);

  const value = useMemo(() => ({ mode, effective, setMode }), [mode, effective]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export const COLOR_MODES = MODES;
export const COLOR_MODE_STORAGE_KEY = STORAGE_KEY;
